import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "preact/hooks";

import { api } from "@/api/client";
import type { DeviceToken } from "@/api/types";
import { ErrorState, LoadingState, useToast } from "@/components/ui";

export function AccountView() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const devices = useQuery({
    queryKey: ["devices"],
    queryFn: async () => {
      const result = await api.devices();
      return Array.isArray(result) ? result : result.devices;
    },
  });

  const remove = async (device: DeviceToken): Promise<void> => {
    try {
      await api.deleteDevice(device.id);
      await queryClient.invalidateQueries({ queryKey: ["devices"] });
      toast(`已撤销 ${device.name}`, "success");
    } catch (error) { toast(error instanceof Error ? error.message : "撤销失败", "error"); }
  };

  const changePassword = async (): Promise<void> => {
    if (newPassword.length < 6) return toast("新密码至少 6 位", "error");
    if (!window.confirm("修改密码会撤销所有 Mac 设备令牌及其他网页会话。之后需要在每台 Mac 上重新配对。继续吗？")) return;
    try {
      const result = await api.changePassword(oldPassword, newPassword);
      setOldPassword(""); setNewPassword("");
      await queryClient.invalidateQueries({ queryKey: ["devices"] });
      toast(`密码已修改，已撤销 ${result.devices_revoked} 台同步设备`, "success");
    } catch (error) { toast(error instanceof Error ? error.message : "修改失败", "error"); }
  };

  return (
    <main class="account-page">
      <header class="page-heading"><h1>账号与设备</h1><p>网页使用安全会话；设备令牌仅供 Mac 同步客户端使用。</p></header>
      <section class="settings-card">
        <h2>修改密码</h2>
        <label>旧密码<input type="password" autoComplete="current-password" value={oldPassword} onInput={(event) => setOldPassword(event.currentTarget.value)} /></label>
        <label>新密码<input type="password" autoComplete="new-password" value={newPassword} onInput={(event) => setNewPassword(event.currentTarget.value)} /></label>
        <button type="button" class="btn btn-primary" onClick={() => void changePassword()}>修改密码</button>
      </section>
      <section class="settings-card">
        <h2>同步设备</h2>
        <p class="muted-text">设备令牌由 Mac App 的“登录并配对”流程自动创建并写入系统 Keychain；网页不会显示或复制令牌。</p>
        {devices.isPending && <LoadingState />}
        {devices.isError && <ErrorState error={devices.error} retry={() => void devices.refetch()} />}
        <ul class="device-list">
          {(devices.data ?? []).map((device) => <li key={device.id}><span><strong>{device.name}</strong><small>{device.created_at}</small></span><button type="button" class="btn btn-ghost" onClick={() => void remove(device)}>撤销</button></li>)}
        </ul>
      </section>
    </main>
  );
}
